import type { Product } from '@/mocks/products';

interface Props {
  product: Product;
}

function DischargeChart({ product }: Props) {
  const pts = product.performance.dischargePoints;
  const minV = 9.0;
  const maxV = 14.0;
  const w = 400;
  const h = 160;
  const padL = 40;
  const padB = 28;
  const padT = 12;
  const padR = 12;
  const chartW = w - padL - padR;
  const chartH = h - padT - padB;

  const toX = (pct: number) => padL + (pct / 100) * chartW;
  const toY = (v: number) => padT + chartH - ((v - minV) / (maxV - minV)) * chartH;

  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(p.percent)} ${toY(p.voltage)}`).join(' ');
  const fillD = `${pathD} L ${toX(100)} ${toY(minV)} L ${toX(0)} ${toY(minV)} Z`;

  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#0EA5E9';
  const color = product.category === 'lithium' ? accentColor : product.category === 'agm' ? '#22c55e' : '#eab308';
  const yTicks = [10, 11, 12, 13, 14];
  const xTicks = [0, 25, 50, 75, 100];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: 160 }}>
      {/* Grid lines */}
      {yTicks.map((v) => (
        <g key={v}>
          <line x1={padL} y1={toY(v)} x2={w - padR} y2={toY(v)} stroke="currentColor" strokeOpacity={0.08} strokeWidth={1} />
          <text x={padL - 4} y={toY(v) + 4} textAnchor="end" fontSize={9} fill="currentColor" fillOpacity={0.4}>{v}V</text>
        </g>
      ))}
      {xTicks.map((pct) => (
        <text key={pct} x={toX(pct)} y={h - 4} textAnchor="middle" fontSize={9} fill="currentColor" fillOpacity={0.4}>{pct}%</text>
      ))}
      {/* Fill */}
      <path d={fillD} fill={color} fillOpacity={0.12} />
      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots */}
      {pts.filter((_, i) => i % 2 === 0).map((p) => (
        <circle key={p.percent} cx={toX(p.percent)} cy={toY(p.voltage)} r={3} fill={color} />
      ))}
    </svg>
  );
}

function StatBar({ label, value, max, unit, color }: { label: string; value: number; max: number; unit: string; color: string }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-current opacity-60">{label}</span>
        <span className="text-xs font-bold" style={{ color }}>{value.toLocaleString()}{unit}</span>
      </div>
      <div className="h-2 rounded-full bg-current opacity-10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function RadialGauge({ value, max, label, unit, color }: { value: number; max: number; label: string; unit: string; color: string }) {
  const pct = Math.min(value / max, 1);
  const r = 36;
  const cx = 48;
  const cy = 48;
  const circumference = 2 * Math.PI * r;
  const arc = circumference * 0.75;
  const offset = arc - pct * arc;
  const startAngle = 135;
  const endAngle = startAngle + 270 * pct;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const x1 = cx + r * Math.cos(toRad(startAngle));
  const y1 = cy + r * Math.sin(toRad(startAngle));
  const x2 = cx + r * Math.cos(toRad(endAngle));
  const y2 = cy + r * Math.sin(toRad(endAngle));
  const largeArc = 270 * pct > 180 ? 1 : 0;

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 96 96" className="w-20 h-20">
        {/* Track */}
        <path
          d={`M ${cx + r * Math.cos(toRad(135))} ${cy + r * Math.sin(toRad(135))} A ${r} ${r} 0 1 1 ${cx + r * Math.cos(toRad(45))} ${cy + r * Math.sin(toRad(45))}`}
          fill="none" stroke="currentColor" strokeOpacity={0.1} strokeWidth={6} strokeLinecap="round"
        />
        {/* Fill */}
        {pct > 0 && (
          <path
            d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
            fill="none" stroke={color} strokeWidth={6} strokeLinecap="round"
          />
        )}
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize={13} fontWeight="bold" fill={color}>{value}{unit}</text>
      </svg>
      <span className="text-xs opacity-50 mt-1 text-center leading-tight">{label}</span>
    </div>
  );
}

export default function PerformanceCharts({ product }: Props) {
  const p = product.performance;
  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#0EA5E9';
  const color = product.category === 'lithium' ? accentColor : product.category === 'agm' ? '#22c55e' : '#eab308';

  return (
    <div className="space-y-6">
      {/* Discharge Curve */}
      <div className="rounded-lg border border-current border-opacity-10 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-bold">Discharge Curve</h4>
            <p className="text-xs opacity-50 mt-0.5">Voltage vs. Depth of Discharge</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${color}20`, color }}>
            {product.voltage}V System
          </span>
        </div>
        <DischargeChart product={product} />
        <div className="flex items-center justify-between mt-3 text-xs opacity-40">
          <span>0% Discharged (Full)</span>
          <span>100% Discharged (Empty)</span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <RadialGauge value={p.efficiency} max={100} label="Round-trip Efficiency" unit="%" color={color} />
        <RadialGauge value={Math.round(p.energyDensity)} max={120} label="Energy Density (Wh/kg)" unit="" color={color} />
        <RadialGauge value={p.selfDischargeMonthly} max={10} label="Self-Discharge / mo" unit="%" color={color} />
      </div>

      {/* Bar Charts */}
      <div className="rounded-lg border border-current border-opacity-10 p-5 space-y-4">
        <h4 className="text-sm font-bold mb-2">Performance Metrics</h4>
        <StatBar label="Cycle Life" value={p.cycleLife} max={2500} unit=" cycles" color={color} />
        <StatBar label="Charge Time (at rated current)" value={p.chargeTime} max={12} unit=" hrs" color={color} />
        <StatBar label="Weight" value={p.weightKg} max={80} unit=" kg" color={color} />
        <StatBar label="Energy Density" value={p.energyDensity} max={120} unit=" Wh/kg" color={color} />
      </div>

      {/* Weight vs Capacity visual */}
      <div className="rounded-lg border border-current border-opacity-10 p-5">
        <h4 className="text-sm font-bold mb-4">Weight &amp; Dimensions</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="text-center">
            <div className="relative mx-auto mb-2" style={{ width: 80, height: 80 }}>
              <div
                className="absolute inset-0 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${color}15`, border: `3px solid ${color}` }}
              >
                <div className="text-center">
                  <p className="text-base font-black" style={{ color }}>{product.weight}</p>
                  <p className="text-[9px] opacity-50">lbs</p>
                </div>
              </div>
            </div>
            <p className="text-xs opacity-50">Total Weight</p>
            <p className="text-xs font-semibold mt-0.5">{p.weightKg} kg</p>
          </div>
          <div className="text-center">
            <div className="relative mx-auto mb-2" style={{ width: 80, height: 80 }}>
              <div
                className="absolute inset-0 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${color}15`, border: `3px solid ${color}` }}
              >
                <div className="text-center px-1">
                  <p className="text-[10px] font-black leading-tight" style={{ color }}>{product.dimensions.split(' x ')[0]}</p>
                  <p className="text-[9px] opacity-40">x {product.dimensions.split(' x ').slice(1).join(' x ')}</p>
                </div>
              </div>
            </div>
            <p className="text-xs opacity-50">Dimensions</p>
            <p className="text-xs font-semibold mt-0.5">(L x W x H)</p>
          </div>
        </div>
      </div>
    </div>
  );
}
